import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Badge,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Group as GroupIcon,
  TrendingUp as TrendingUpIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  Security as SecurityIcon,
  Star as StarIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import apiClient from '../../request/http-common';

const JointInvestment = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useSelector((state) => state.user.user);
  const [selectedInvestment, setSelectedInvestment] = useState(null);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [contributionAmount, setContributionAmount] = useState('');
  const [activeTab, setActiveTab] = useState('available');

  // Form state for creating copy trading
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    target_amount: '',
    minimum_contribution: '',
    maximum_contribution: '',
    expected_return: '',
    investment_plan_id: '',
    max_participants: '',
    profit_sharing_type: 'percentage',
    management_fee: '',
    risk_level: 'medium',
    category: '',
    start_date: '',
    end_date: '',
    is_public: true,
    auto_close_on_target: false
  });

  // Get copy trading from Redux (traders)
  const availableInvestments = useSelector((state) => state.traders.traders) || [];
  const loadingAvailable = false; // No loading since data is from Redux
  const loadingMy = false; // No loading since data is from Redux
  
  // Mock user's investments for now - this would come from user's participation data
  const myInvestments = {
    created_investments: [],
    participated_investments: []
  };

  // Join copy trading mutation
  const joinMutation = useMutation(
    async ({ id, amount }) => {
      const response = await apiClient.post(`/api/join-joint-investment/${id}`, {
        contribution_amount: amount
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('joint-investments');
        queryClient.invalidateQueries('my-joint-investments');
        setJoinDialogOpen(false);
        setSelectedInvestment(null);
        setContributionAmount('');
      }
    }
  );

  // Create copy trading mutation
  const createMutation = useMutation(
    async (formData) => {
      const response = await apiClient.post('/api/create-joint-investment', formData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('joint-investments');
        queryClient.invalidateQueries('my-joint-investments');
        setCreateDialogOpen(false);
        setCreateForm({
          name: '',
          description: '',
          target_amount: '',
          minimum_contribution: '',
          maximum_contribution: '',
          expected_return: '',
          investment_plan_id: '',
          max_participants: '',
          profit_sharing_type: 'percentage',
          management_fee: '',
          risk_level: 'medium',
          category: '',
          start_date: '',
          end_date: '',
          is_public: true,
          auto_close_on_target: false
        });
      }
    }
  );

  const formatPrice = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);

  const formatDate = (date) => new Date(date).toLocaleDateString();

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'low': return '#02C076';
      case 'medium': return '#FFD700';
      case 'high': return '#F84960';
      default: return '#B7BDC6';
    }
  };

  const getProfitSharingLabel = (type) => {
    switch (type) {
      case 'percentage': return 'Percentage Based';
      case 'equal': return 'Equal Split';
      case 'weighted': return 'Weighted';
      default: return type;
    }
  };

  const handleJoinInvestment = (investment) => {
    setSelectedInvestment(investment);
    setContributionAmount(investment.minimum_contribution);
    setJoinDialogOpen(true);
  };

  const handleCreateInvestment = () => {
    createMutation.mutate(createForm);
  };

  const handleJoinConfirm = () => {
    if (selectedInvestment && contributionAmount) {
      joinMutation.mutate({
        id: selectedInvestment.id,
        amount: parseFloat(contributionAmount)
      });
    }
  };

  const renderInvestmentCard = (trader, isMyInvestment = false) => (
    <Card sx={{
      marginX: '5px',
      borderRadius: '12px',
      height: '100%',
      paddingX: '10px',
      background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
      border: '1px solid #2B3139',
      transition: 'all 0.3s ease',
      '&:hover': {
        borderColor: '#F7931A',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 20px rgba(247, 147, 26, 0.2)'
      }
    }}>
      {/* Status chip for joined investments */}
      {isMyInvestment && (
        <Chip 
          mb={-4} 
          sx={{
            transform: 'translate(110px, -1px)', 
            color: '#02C076', 
            position: 'relative',
            background: 'rgba(2, 192, 118, 0.1)',
            fontSize: '10px'
          }} 
          size="small" 
          icon={<CheckCircleIcon sx={{color: '#02C076'}} />} 
          label="Invested" 
        />
      )}

      <Box sx={{
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        paddingY: '12px', 
        paddingX: '5px'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <Avatar 
            sx={{ 
              bgcolor: '#d7251e',
              width: 48,
              height: 48,
              borderRadius: '12px',
              border: '2px solid #F7931A',
              boxShadow: '0 4px 12px rgba(247, 147, 26, 0.3)'
            }} 
            src={trader.image}
            variant="rounded"
          >
            <GroupIcon sx={{ color: '#FFFFFF' }} />
          </Avatar>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'flex-start',
            marginLeft: '12px',
            flex: 1
          }}>
            <Typography sx={{
              fontSize: 14, 
              fontWeight: 600,
              color: '#FFFFFF',
              textTransform: 'capitalize',
              lineHeight: 1.2
            }} 
            variant="subtitle2" 
            component="div">
              {trader.trader_name}
            </Typography>

            <Typography sx={{
              fontSize: 11, 
              color: '#B7BDC6',
              textTransform: 'capitalize',
              marginTop: '2px'
            }} 
            variant="caption" 
            component="div">
              {trader.level}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginTop: '2px' }}>
              <Chip
                label={trader.risk_score > 4 ? 'High Risk' : trader.risk_score > 2 ? 'Medium Risk' : 'Low Risk'}
                size="small"
                sx={{
                  background: trader.risk_score > 4 ? 'rgba(248, 73, 96, 0.2)' : trader.risk_score > 2 ? 'rgba(255, 215, 0, 0.2)' : 'rgba(2, 192, 118, 0.2)',
                  color: trader.risk_score > 4 ? '#F84960' : trader.risk_score > 2 ? '#FFD700' : '#02C076',
                  fontSize: '9px',
                  height: '18px',
                  fontWeight: 600
                }}
              />
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Button 
            color='primary' 
            variant='contained' 
            size='small'
            onClick={() => navigate(`/view-project/${trader.id}`)}
            sx={{
              background: 'linear-gradient(90deg, #F7931A 0%, #FFD700 100%)',
              color: '#000',
              fontWeight: 600,
              fontSize: '11px',
              minWidth: '60px',
              height: '28px',
              '&:hover': {
                background: 'linear-gradient(90deg, #E8841A 0%, #E6C200 100%)'
              }
            }}
          >
            Invest
          </Button>
        </Box>
      </Box>
      
      <Divider sx={{ borderColor: '#2B3139' }} />

      <CardContent sx={{ paddingTop: '12px !important', paddingBottom: '16px !important' }}>
        {/* Progress bar */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '11px' }}>
              Progress
            </Typography>
            <Typography variant="caption" sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '11px' }}>
              {trader.gain}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(parseFloat(trader.gain) || 0, 100)}
            sx={{
              height: 4,
              borderRadius: 2,
              backgroundColor: '#2B3139',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(90deg, #F7931A 0%, #FFD700 100%)',
                borderRadius: 2
              }
            }}
          />
        </Box>

        {/* Key metrics in a row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '10px' }}>
              ROI {trader.duration}
            </Typography>
            <Typography sx={{ 
              color: '#02C076', 
              fontWeight: 600, 
              fontSize: 12,
              lineHeight: 1.2
            }} 
            variant="subtitle2" 
            component="div">
              {trader.gain}%
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '10px' }}>
              Investors
            </Typography>
            <Typography sx={{ 
              color: '#FFFFFF', 
              fontWeight: 600, 
              fontSize: 12,
              lineHeight: 1.2
            }} 
            variant="subtitle2" 
            component="div">
              {trader.copiers || 0}
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '10px' }}>
              Min Amount
            </Typography>
            <Typography sx={{ 
              color: '#FFFFFF', 
              fontWeight: 600, 
              fontSize: 12,
              lineHeight: 1.2
            }} 
            variant="subtitle2" 
            component="div">
              {formatPrice(trader.min_amount || 100)}
            </Typography>
          </Box>
        </Box>

        {/* Additional info row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <MoneyIcon sx={{ fontSize: 12, color: '#B7BDC6' }} />
            <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '10px' }}>
              {trader.commission || 2}% Fee
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ScheduleIcon sx={{ fontSize: 12, color: '#B7BDC6' }} />
            <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '10px' }}>
              {trader.duration}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <>
      <div>
        <Typography mb={-8} mt={4} px={3} sx={{ marginTop: '75px', fontWeight: 'bold', fontSize: 20, textAlign: 'center'  }} gutterBottom variant="h6" component="div">
          COPY TRADING
        </Typography>
      
      {/* Tabs */}
      <Box sx={{ mb: 3, mt: 4, px: 2 }}>
        <Stack direction="row" spacing={1} justifyContent="center">
          <Button
            variant={activeTab === 'available' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('available')}
            size="small"
            sx={{
              background: activeTab === 'available' ? '#F7931A' : 'transparent',
              borderColor: '#F7931A',
              color: activeTab === 'available' ? '#000' : '#F7931A',
              fontSize: '11px',
              '&:hover': {
                background: activeTab === 'available' ? '#E8841A' : 'rgba(247, 147, 26, 0.1)'
              }
            }}
          >
            Available ({availableInvestments?.length || 0})
          </Button>
          <Button
            variant={activeTab === 'my' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('my')}
            size="small"
            sx={{
              background: activeTab === 'my' ? '#F7931A' : 'transparent',
              borderColor: '#F7931A',
              color: activeTab === 'my' ? '#000' : '#F7931A',
              fontSize: '11px',
              '&:hover': {
                background: activeTab === 'my' ? '#E8841A' : 'rgba(247, 147, 26, 0.1)'
              }
            }}
          >
            My Investments ({myInvestments ? (myInvestments.created_investments?.length || 0) + (myInvestments.participated_investments?.length || 0) : 0})
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            size="small"
            sx={{
              background: 'linear-gradient(90deg, #F7931A 0%, #FFD700 100%)',
              color: '#000',
              fontWeight: 600,
              fontSize: '11px',
              '&:hover': {
                background: 'linear-gradient(90deg, #E8841A 0%, #E6C200 100%)'
              }
            }}
          >
            Create
          </Button>
        </Stack>
      </Box>

      {/* Investment Grid */}
      {activeTab === 'available' && availableInvestments?.length > 0 && (
        <Grid container sx={{ marginTop: '75px'}} px={2} mt={4} mb={4} rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          {availableInvestments.map((trader) => (
            <Grid key={trader.id} item xs={12} md={4}>
              {renderInvestmentCard(trader)}
            </Grid>
          ))}
        </Grid>
      )}

      {activeTab === 'my' && (
        <>
          {/* Created Investments */}
          {myInvestments?.created_investments?.length > 0 && (
            <Grid container sx={{ marginTop: '75px'}} px={2} mt={4} mb={4} rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
              {myInvestments.created_investments.map((investment) => (
                <Grid key={investment.id} item xs={12} md={4}>
                  {renderInvestmentCard(investment, true)}
                </Grid>
              ))}
            </Grid>
          )}

          {/* Participated Investments */}
          {myInvestments?.participated_investments?.length > 0 && (
            <Grid container sx={{ marginTop: '75px'}} px={2} mt={4} mb={4} rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
              {myInvestments.participated_investments.map((investment) => (
                <Grid key={investment.id} item xs={12} md={4}>
                  {renderInvestmentCard(investment, true)}
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* Loading States */}
      {activeTab === 'available' && loadingAvailable && (
        <Box sx={{ textAlign: 'center', padding: '40px' }}>
          <Typography sx={{ color: '#B7BDC6' }}>Loading available copy trading...</Typography>
        </Box>
      )}

      {activeTab === 'my' && loadingMy && (
        <Box sx={{ textAlign: 'center', padding: '40px' }}>
          <Typography sx={{ color: '#B7BDC6' }}>Loading your copy trading...</Typography>
        </Box>
      )}

      {/* Empty States */}
      {activeTab === 'available' && !loadingAvailable && (!availableInvestments || availableInvestments.length === 0) && (
        <Box sx={{ textAlign: 'center', padding: '40px' }}>
          <GroupIcon sx={{ fontSize: 64, color: '#6C7293', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#6C7293', mb: 1 }}>
            No Copy Trading Available
          </Typography>
          <Typography sx={{ color: '#6C7293' }}>
            Be the first to create a copy trading opportunity!
          </Typography>
        </Box>
      )}

      {activeTab === 'my' && !loadingMy && (!myInvestments?.created_investments?.length && !myInvestments?.participated_investments?.length) && (
        <Box sx={{ textAlign: 'center', padding: '40px' }}>
          <GroupIcon sx={{ fontSize: 64, color: '#6C7293', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#6C7293', mb: 1 }}>
            No Copy Trading Yet
          </Typography>
          <Typography sx={{ color: '#6C7293', mb: 2 }}>
            Start by creating a copy trading or joining an existing one!
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              background: 'linear-gradient(90deg, #F7931A 0%, #FFD700 100%)',
              color: '#000',
              fontWeight: 600
            }}
          >
            Create Copy Trading
          </Button>
        </Box>
      )}
      </div>

      {/* Join Dialog */}
      <Dialog open={joinDialogOpen} onClose={() => setJoinDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#FFFFFF', background: '#161A1E' }}>
          Join Copy Trading
        </DialogTitle>
        <DialogContent sx={{ background: '#161A1E', color: '#FFFFFF' }}>
          {selectedInvestment && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {selectedInvestment.name}
              </Typography>
              <Typography sx={{ color: '#B7BDC6', mb: 3 }}>
                {selectedInvestment.description}
              </Typography>
              
              <TextField
                fullWidth
                label="Contribution Amount"
                type="number"
                value={contributionAmount}
                onChange={(e) => setContributionAmount(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#2B3139' },
                    '&:hover fieldset': { borderColor: '#F7931A' },
                    '&.Mui-focused fieldset': { borderColor: '#F7931A' }
                  },
                  '& .MuiInputLabel-root': { color: '#B7BDC6' }
                }}
                InputProps={{
                  startAdornment: <Typography sx={{ color: '#B7BDC6', mr: 1 }}>$</Typography>
                }}
              />
              
              <Alert severity="info" sx={{ background: 'rgba(247, 147, 26, 0.1)', color: '#F7931A' }}>
                Minimum: {formatPrice(selectedInvestment.minimum_contribution)} | 
                Maximum: {formatPrice(selectedInvestment.maximum_contribution)}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ background: '#161A1E' }}>
          <Button onClick={() => setJoinDialogOpen(false)} sx={{ color: '#B7BDC6' }}>
            Cancel
          </Button>
          <Button
            onClick={handleJoinConfirm}
            disabled={joinMutation.isLoading}
            sx={{
              background: '#F7931A',
              color: '#000',
              '&:hover': { background: '#E8841A' }
            }}
          >
            {joinMutation.isLoading ? 'Joining...' : 'Join Copy Trading'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#FFFFFF', background: '#161A1E' }}>
          Create Copy Trading
        </DialogTitle>
        <DialogContent sx={{ background: '#161A1E', color: '#FFFFFF' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Investment Name"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { color: '#FFFFFF' } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Category"
                value={createForm.category}
                onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { color: '#FFFFFF' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { color: '#FFFFFF' } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Target Amount"
                type="number"
                value={createForm.target_amount}
                onChange={(e) => setCreateForm({ ...createForm, target_amount: e.target.value })}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { color: '#FFFFFF' } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Minimum Contribution"
                type="number"
                value={createForm.minimum_contribution}
                onChange={(e) => setCreateForm({ ...createForm, minimum_contribution: e.target.value })}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { color: '#FFFFFF' } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Maximum Contribution"
                type="number"
                value={createForm.maximum_contribution}
                onChange={(e) => setCreateForm({ ...createForm, maximum_contribution: e.target.value })}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { color: '#FFFFFF' } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Expected Return (%)"
                type="number"
                value={createForm.expected_return}
                onChange={(e) => setCreateForm({ ...createForm, expected_return: e.target.value })}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { color: '#FFFFFF' } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Management Fee (%)"
                type="number"
                value={createForm.management_fee}
                onChange={(e) => setCreateForm({ ...createForm, management_fee: e.target.value })}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { color: '#FFFFFF' } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel sx={{ color: '#B7BDC6' }}>Risk Level</InputLabel>
                <Select
                  value={createForm.risk_level}
                  onChange={(e) => setCreateForm({ ...createForm, risk_level: e.target.value })}
                  sx={{ color: '#FFFFFF' }}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel sx={{ color: '#B7BDC6' }}>Profit Sharing</InputLabel>
                <Select
                  value={createForm.profit_sharing_type}
                  onChange={(e) => setCreateForm({ ...createForm, profit_sharing_type: e.target.value })}
                  sx={{ color: '#FFFFFF' }}
                >
                  <MenuItem value="percentage">Percentage Based</MenuItem>
                  <MenuItem value="equal">Equal Split</MenuItem>
                  <MenuItem value="weighted">Weighted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max Participants"
                type="number"
                value={createForm.max_participants}
                onChange={(e) => setCreateForm({ ...createForm, max_participants: e.target.value })}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { color: '#FFFFFF' } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Investment Plan ID"
                type="number"
                value={createForm.investment_plan_id}
                onChange={(e) => setCreateForm({ ...createForm, investment_plan_id: e.target.value })}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { color: '#FFFFFF' } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={createForm.start_date}
                onChange={(e) => setCreateForm({ ...createForm, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { color: '#FFFFFF' } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={createForm.end_date}
                onChange={(e) => setCreateForm({ ...createForm, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { color: '#FFFFFF' } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ background: '#161A1E' }}>
          <Button onClick={() => setCreateDialogOpen(false)} sx={{ color: '#B7BDC6' }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateInvestment}
            disabled={createMutation.isLoading}
            sx={{
              background: '#F7931A',
              color: '#000',
              '&:hover': { background: '#E8841A' }
            }}
          >
            {createMutation.isLoading ? 'Creating...' : 'Create Investment'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default JointInvestment; 